// Import package components
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { AppContexts } from "../../../providers";
import { isIdentityVerificationEnabled } from "../../../utils/identityVerification";

const macrosValues = [
  { value: "[CompanyName]", label: "Company Name" },
  { value: "[FirstName]", label: "Guest's first name" },
  { value: "[Email]", label: "Guest Email" },
  { value: "[LastName]", label: "Guest's last name" },
  { value: "[PropertyId]", label: "Property Id" },
  { value: "[CheckIn]", label: "Check-in date" },
  { value: "[CheckOut]", label: "Check-out date" },
  { value: "[PhoneNumber]", label: "Guest Phone Number" },
  { value: "[ReservationId]", label: "Reservation Number" },
  { value: "[PropertyAddress]", label: "Property address" },
  { value: "[DoorCode]", label: "Reservation's door code" },
  { value: "[PropertyGuideLink]", label: "Link to home guide" },
  { value: "[DestinationGuideLink]", label: "Link to area guide" },
  { value: "[FiveStarLink]", label: "Five-star review link" },
  { value: "[GuestCheckIn]", label: "Link to check-in" },
  { value: "[GuestIDVCheckIn]", label: "Link to ID verification check-in" },
  { value: "[GuestCheckOut]", label: "Link to check-out" },
  { value: "[EarlyCheckIn]", label: "Link to request early check-in" },
  { value: "[LateCheckOut]", label: "Link to request late check-out" },
];
macrosValues.sort((a, b) => (a.value > b.value ? 1 : -1));

const MacrosReservationKeys = ({
  onMacroClick,
  allowIdentityVerification = false,
}) => {
  let { allSurveys, getSurveyList } = useContext(AppContexts.SurveysContext);
  let { settings, get } = useContext(AppContexts.SettingsContext);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openAbove, setOpenAbove] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    getSurveyList();
    get(
      allowIdentityVerification
        ? ["custom_macros", "integration"]
        : ["custom_macros"],
    );
  }, [allowIdentityVerification]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Focus search when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      if (prev) {
        setSearch("");
        return false;
      }
      // Decide if dropdown should open above or below
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        setOpenAbove(spaceBelow < 400);
      }
      return true;
    });
  };

  const handleSelect = useCallback((value) => {
    if (onMacroClick) onMacroClick(value);
    setIsOpen(false);
    setSearch("");
  }, [onMacroClick]);

  const q = search.toLowerCase();

  const filteredMacros = macrosValues.filter(
    (m) =>
      (m.value !== "[GuestIDVCheckIn]" ||
        (allowIdentityVerification && isIdentityVerificationEnabled(settings))) &&
      (m.value.toLowerCase().includes(q) || m.label.toLowerCase().includes(q))
  );
  const filteredCustom = settings.custom_macros?.filter(
    (m) => m.macro?.toLowerCase().includes(q) || m.label?.toLowerCase().includes(q)
  );
  const filteredSurveys = allSurveys?.filter(
    (s) =>
      !s.deleted &&
      (`[Survey-${s.survey_id}]`.toLowerCase().includes(q) ||
        s.name?.toLowerCase().includes(q))
  );

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <span
        ref={triggerRef}
        uk-tooltip="View Macros"
        className="macro-icon"
        onClick={toggleDropdown}
      >
        <img alt="macro-icon" src="/images/macro-icon.svg" />
      </span>
      {isOpen && (
        <div id="macros-reservation" ref={dropdownRef} className={openAbove ? "open-above" : ""}>
          <div className="macros-search-wrapper">
            <input
              ref={searchRef}
              type="text"
              className="uk-input macros-search-input"
              placeholder="Search macros..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search ? (
              <span
                className="macros-search-clear"
                uk-icon="icon: close; ratio: 0.7"
                onClick={() => { setSearch(""); searchRef.current?.focus(); }}
              />
            ) : (
              <span uk-icon="icon: search; ratio: 0.8" className="macros-search-icon" />
            )}
          </div>
          <ul className="uk-nav uk-dropdown-nav tags-selector macros-list">
            {filteredMacros.length > 0 && (
              <div className="macros-section-label">Booking Macros</div>
            )}
            {filteredMacros.map((macros, index) => (
              <li
                key={`macros_${index}`}
                className="messages-li"
                onClick={() => handleSelect(macros.value)}
              >
                <span className="macro-value">{macros.value}</span>{" "}
                <span className="macro-label">{macros.label}</span>
              </li>
            ))}
            {filteredCustom?.length > 0 && (
              <div className="macros-section-label">Property Macros</div>
            )}
            {filteredCustom?.map((macros, index) => (
              <li
                key={`custom_macros_${index}`}
                className="messages-li"
                onClick={() => handleSelect(macros.macro)}
              >
                <span className="macro-value">{macros.macro}</span>
                <span className="macro-label">{macros.label}</span>
              </li>
            ))}
            {filteredSurveys?.length > 0 && (
              <div className="macros-section-label">Survey Macros</div>
            )}
            {filteredSurveys?.map((survey, index) => (
              <li
                key={`survey_${index}`}
                className="messages-li"
                onClick={() => handleSelect(`[Survey-${survey.survey_id}]`)}
              >
                <span className="macro-value">{`[Survey-${survey.survey_id}]`}</span>
                <span className="macro-label">
                  {survey.name?.length > 30
                    ? `${survey.name.slice(0, 30)}...`
                    : survey.name}
                </span>
              </li>
            ))}
            {filteredMacros.length === 0 && !filteredCustom?.length && !filteredSurveys?.length && (
              <li className="macros-no-results">No macros found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MacrosReservationKeys;
